import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import type { TreeNode } from '../api/types.js';

// ---------------------------------------------------------------------------
// <sysml-file-explorer> -- Project panel with file tree + model structure
// ---------------------------------------------------------------------------

@customElement('sysml-file-explorer')
export class SysmlFileExplorer extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        font-size: var(--r5-text-xs);
        background: var(--r5-bg, var(--r5-surface));
      }

      /* ── Header ── */
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px;
        font-size: var(--r5-text-2xs);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--r5-text-muted, #888);
        flex-shrink: 0;
      }
      .header-actions {
        display: flex;
        gap: 2px;
      }
      .header-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border: none;
        background: none;
        color: var(--r5-text-muted, #888);
        cursor: pointer;
        border-radius: 4px;
        transition: all 0.15s;
      }
      .header-btn:hover {
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
        color: var(--r5-primary, #3b82f6);
      }

      /* ── Filter bar ── */
      .filter-bar {
        display: flex;
        align-items: center;
        margin: 0 8px 6px;
        padding: 0 8px;
        gap: 6px;
        border: 1px solid var(--r5-border, #e5e5e5);
        border-radius: var(--r5-radius-md, 6px);
        background: var(--r5-surface, #fff);
        flex-shrink: 0;
        transition: border-color 0.15s, box-shadow 0.15s;
      }
      .filter-bar:focus-within {
        border-color: var(--r5-primary, #3b82f6);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--r5-primary) 15%, transparent);
      }
      .filter-bar svg {
        width: 12px;
        height: 12px;
        color: var(--r5-text-muted, #888);
        flex-shrink: 0;
        opacity: 0.5;
      }
      .filter-bar:focus-within svg {
        color: var(--r5-primary, #3b82f6);
        opacity: 0.8;
      }
      .filter-input {
        flex: 1;
        padding: 5px 0;
        border: none;
        font-size: var(--r5-text-2xs);
        background: transparent;
        color: var(--r5-text, #333);
        outline: none;
      }
      .filter-input::placeholder {
        color: var(--r5-text-muted, #888);
        opacity: 0.6;
      }

      /* ── Scrollable content ── */
      .content {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 8px;
      }
      .content::-webkit-scrollbar {
        width: 8px;
      }
      .content::-webkit-scrollbar-track {
        background: transparent;
      }
      .content::-webkit-scrollbar-thumb {
        background-color: rgba(121, 121, 121, 0.4);
      }
      .content::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }
      .content::-webkit-scrollbar-corner {
        background: transparent;
      }

      /* ── Section header ── */
      .section-header {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px 4px;
        font-size: var(--r5-font-size-badge);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--r5-text-muted, #888);
        user-select: none;
        cursor: pointer;
        transition: color 0.1s;
      }
      .section-header:hover {
        color: var(--r5-text, #333);
      }
      .section-header .chevron-sm {
        width: 10px;
        height: 10px;
        flex-shrink: 0;
        transition: transform 0.15s;
      }
      .section-header .chevron-sm.expanded {
        transform: rotate(90deg);
      }
      .section-line {
        flex: 1;
        height: 1px;
        background: color-mix(in srgb, var(--r5-border) 40%, transparent);
      }
      .section-count {
        font-size: var(--r5-font-size-badge);
        font-weight: 500;
        color: var(--r5-text-muted, #888);
        opacity: 0.6;
        font-variant-numeric: tabular-nums;
      }

      /* ── File tree nodes ── */
      .tree-node {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 4px 12px;
        margin: 0 4px;
        cursor: pointer;
        user-select: none;
        transition: all 0.1s;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        border-radius: var(--r5-radius-sm, 4px);
      }
      .tree-node:hover {
        background: color-mix(in srgb, var(--r5-primary) 6%, transparent);
      }
      .tree-node.selected {
        background: color-mix(in srgb, var(--r5-primary) 14%, transparent);
        color: var(--r5-primary, #3b82f6);
      }
      .tree-node .icon {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--r5-text-muted, #888);
      }
      .tree-node.selected .icon {
        color: var(--r5-primary, #3b82f6);
      }
      .tree-node .icon svg {
        width: 14px;
        height: 14px;
      }
      .tree-node .label {
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
      }

      /* ── Empty state ── */
      .empty-state {
        padding: 16px 12px;
        text-align: center;
        color: var(--r5-text-muted, #888);
        font-size: var(--r5-text-2xs);
        opacity: 0.7;
      }
    `,
  ];

  @property({ type: Array }) nodes: TreeNode[] = [];
  @property({ type: String }) selectedId = '';
  @state() private _filter = '';
  @state() private _expandedFolders = new Set<string>();
  @state() private _filesCollapsed = false;

  override render() {
    const filtered = this._filter
      ? this.nodes.filter(n => this._matchFilter(n, this._filter.toLowerCase()))
      : this.nodes;

    return html`
      <div class="header">
        <span>Explorer</span>
        <div class="header-actions">
          <button class="header-btn" title="Collapse all" @click=${this._collapseAll}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M4 6l4 4 4-4"/>
            </svg>
          </button>
          <button class="header-btn" title="New file" @click=${this._onNewFile}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1Z"/>
              <path d="M9 1v4h4"/>
              <path d="M8 9v4M6 11h4"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="filter-bar">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="6.5" cy="6.5" r="4.5"/>
          <path d="M10 10l4 4"/>
        </svg>
        <input
          class="filter-input"
          type="text"
          placeholder="Filter elements..."
          .value=${this._filter}
          @input=${(e: InputEvent) => (this._filter = (e.target as HTMLInputElement).value)}
        />
      </div>

      <div class="content">
        <!-- Files Section -->
        <div class="section-header" @click=${() => (this._filesCollapsed = !this._filesCollapsed)}>
          <svg class="chevron-sm ${this._filesCollapsed ? '' : 'expanded'}" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 4l4 4-4 4"/>
          </svg>
          Files
          <span class="section-line"></span>
          <span class="section-count">${filtered.length}</span>
        </div>
        ${this._filesCollapsed
          ? nothing
          : filtered.length === 0
            ? html`<div class="empty-state">No documents</div>`
            : filtered.map(node => this._renderFileNode(node, 0))}
      </div>
    `;
  }

  // -- File tree rendering --------------------------------------------------

  private _renderFileNode(node: TreeNode, depth: number): unknown {
    if (node.type === 'folder') {
      const expanded = this._expandedFolders.has(node.id);
      return html`
        <div
          class="tree-node"
          style="padding-left: ${12 + depth * 16}px"
          @click=${() => this._toggleFolder(node.id)}
        >
          <span class="icon">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
              ${expanded
                ? html`<path d="M2 4h4l2 2h6v8H2V4Z"/><path d="M5 6v8"/>`
                : html`<path d="M2 4h4l2 2h6v8H2V4Z"/>`}
            </svg>
          </span>
          <span class="label">${node.name}</span>
        </div>
        ${expanded ? node.children.map(c => this._renderFileNode(c, depth + 1)) : nothing}
      `;
    }

    return html`
      <div
        class="tree-node ${node.id === this.selectedId ? 'selected' : ''}"
        style="padding-left: ${12 + depth * 16}px"
        @click=${() => this._selectFile(node)}
      >
        <span class="icon">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M9 1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L9 1Z"/>
            <path d="M9 1v4h4"/>
          </svg>
        </span>
        <span class="label">${node.name}</span>
      </div>
    `;
  }

  // -- Helpers & events -----------------------------------------------------

  private _matchFilter(node: TreeNode, filter: string): boolean {
    if (node.name.toLowerCase().includes(filter)) return true;
    if (node.children?.some(c => this._matchFilter(c, filter))) return true;
    return false;
  }

  private _toggleFolder(id: string): void {
    if (this._expandedFolders.has(id)) {
      this._expandedFolders.delete(id);
    } else {
      this._expandedFolders.add(id);
    }
    this.requestUpdate();
  }

  private _collapseAll(): void {
    this._expandedFolders.clear();
    this.requestUpdate();
  }

  private _selectFile(node: TreeNode): void {
    this.dispatchEvent(
      new CustomEvent('fileSelect', {
        detail: { id: node.id, name: node.name },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onNewFile(): void {
    this.dispatchEvent(
      new CustomEvent('fileCreate', {
        detail: {},
        bubbles: true,
        composed: true,
      }),
    );
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-file-explorer': SysmlFileExplorer;
  }
}
